(() => {
  'use strict';
  if (window.castlewoodVanitySummary) return;
  window.castlewoodVanitySummary = true;
  const root = () => window.Shopify?.routes?.root || '/';
  const node = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  };
  const money = (amount, currency) => new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency }).format(amount / 100);
  const read = row => {
    const rawProperties = JSON.parse(row.dataset.cwVanityProperties);
    const properties = Array.isArray(rawProperties) ? Object.fromEntries(rawProperties) : rawProperties;
    const quantity = Number(row.dataset.cwVanityQuantity);
    const total = Number(row.dataset.cwVanityTotal);
    if (!properties || typeof properties !== 'object' || !Number.isSafeInteger(quantity) || quantity < 1 || !Number.isSafeInteger(total) || total < 0) throw new Error('Invalid vanity item');
    return { row, properties, quantity, total, key: row.dataset.cwVanityKey, currency: row.dataset.cwCurrency || 'USD' };
  };
  const imageData = item => {
    const image = item.row.querySelector('img');
    return image ? [image.getAttribute('src') || '', image.getAttribute('srcset') || ''] : ['', ''];
  };
  const cabinet = item => String(item.properties._cw_vanity_line || '').endsWith('-cabinet');
  function build(items, order) {
    const cabinets = items.filter(cabinet).sort((a, b) => Number(String(a.properties._cw_vanity_line).match(/^m(\d+)-/)?.[1] || 0) - Number(String(b.properties._cw_vanity_line).match(/^m(\d+)-/)?.[1] || 0));
    if (!cabinets.length || items.some(item => item.properties._cw_vanity_order !== order || item.currency !== items[0].currency)) return null;
    const currency = items[0].currency;
    const finishes = [...new Set(cabinets.map(item => String(item.properties['Door style / finish'] || '').split(' / ').slice(1).join(' / ').replace(/ \([^)]*\)$/, '')))];
    const styles = [...new Set(cabinets.map(item => String(item.properties['Door style / finish'] || '').split(' / ')[0]))];
    const dimensions = cabinets.map(item => String(item.properties['Cabinet dimensions'] || '').match(/^([\d.]+) W × ([\d.]+) H × ([\d.]+) D in$/));
    const total = items.reduce((sum, item) => sum + item.total, 0);
    const tr = node('tr', 'cw-vanity-summary');
    tr.dataset.cwVanitySummary = order;
    const td = node('td');
    td.colSpan = items[0].row.closest('cart-drawer-items') ? 4 : 5;
    const card = node('article', 'cw-vanity-card');
    if (cabinets.some(item => imageData(item)[0])) {
      const multiple = cabinets.length > 1;
      if (multiple) card.classList.add('cw-vanity-card--multiple');
      const gallery = node('ul', 'cw-vanity-card__gallery');
      gallery.setAttribute('aria-label', 'Cabinets in your vanity');
      if (multiple) gallery.tabIndex = 0;
      for (const item of cabinets) {
        const [src, srcset] = imageData(item);
        const tile = node('li', 'cw-vanity-card__tile');
        const code = String(item.properties['Vanity cabinet'] || '').split(' · ').pop() || 'Cabinet';
        const finish = String(item.properties['Door style / finish'] || '');
        if (src) {
          const preview = node('img', 'cw-vanity-card__image');
          preview.src = src;
          if (srcset) preview.srcset = srcset;
          preview.sizes = multiple ? '120px' : '(max-width: 749px) 108px, 160px';
          preview.width = 160;
          preview.height = 160;
          preview.loading = 'eager';
          preview.decoding = 'async';
          preview.alt = `${code}${finish ? ' · ' + finish : ''}`;
          tile.append(preview);
        } else {
          tile.append(node('span', 'cw-vanity-card__image-unavailable', 'Image unavailable'));
        }
        tile.append(node('span', 'cw-vanity-card__image-label', `${code}${item.quantity > 1 ? ' × ' + item.quantity : ''}`));
        gallery.append(tile);
      }
      card.append(gallery);
    }
    const content = node('div', 'cw-vanity-card__content');
    content.append(node('h3', 'cw-vanity-card__title', `${styles.length === 1 && styles[0] ? styles[0] + ' ' : ''}vanity`));
    const specs = [];
    if (finishes.length === 1 && finishes[0]) specs.push(finishes[0]);
    else if (finishes.length > 1) specs.push('Mixed finishes');
    if (dimensions.every(Boolean) && dimensions.every(dim => dim[2] === dimensions[0][2] && dim[3] === dimensions[0][3])) {
      const width = dimensions.reduce((sum, dim, index) => sum + Number(dim[1]) * cabinets[index].quantity, 0);
      specs.push(`${width}″ W × ${dimensions[0][3]}″ D × ${dimensions[0][2]}″ H`);
    }
    const assemblies = [...new Set(cabinets.map(item => item.properties.Assembly))];
    if (assemblies.length === 1 && assemblies[0]) specs.push(assemblies[0] === 'Unassembled' ? 'Ready to assemble' : assemblies[0]);
    content.append(node('p', 'cw-vanity-card__specs', specs.join(' · ')));
    const count = cabinets.reduce((sum, item) => sum + item.quantity, 0);
    const price = node('div', 'cw-vanity-card__price');
    price.append(node('strong', '', money(total, currency)), node('span', '', `${count} cabinet${count === 1 ? '' : 's'}${items.some(item => !cabinet(item)) ? ' · services included' : ''}`));
    content.append(price);
    content.append(node('p', 'cw-vanity-card__scope', 'Cabinets only. Countertops, basins and decorative hardware excluded.'));
    const details = node('details', 'cw-vanity-card__breakdown');
    details.append(node('summary', '', 'View cabinet breakdown'));
    const list = node('ul');
    for (const item of cabinets) {
      const prefix = String(item.properties._cw_vanity_line).replace(/-cabinet$/, '-');
      const services = items.filter(other => !cabinet(other) && String(other.properties._cw_vanity_line).startsWith(prefix));
      let label = String(item.properties['Vanity cabinet'] || '').split(' · ').pop() || 'Cabinet';
      const finish = String(item.properties['Door style / finish'] || '').split(' / ').slice(1).join(' / ');
      if (finish) label += ` · ${finish}`;
      if (services.some(service => String(service.properties._cw_vanity_line).endsWith('-assembly'))) label += ' · assembly included';
      if (services.some(service => String(service.properties._cw_vanity_line).endsWith('-depth'))) label += ' · depth modification included';
      const li = node('li');
      const cabinetTotal = item.total + services.reduce((sum, service) => sum + service.total, 0);
      li.append(node('span', '', `${label}${item.quantity > 1 ? ' × ' + item.quantity : ''}`), node('span', '', money(cabinetTotal, currency)));
      list.append(li);
    }
    details.append(list);
    content.append(details);
    const button = node('button', 'cw-vanity-card__remove', 'Remove vanity');
    button.type = 'button';
    button.dataset.cwRemoveVanity = order;
    button.setAttribute('aria-label', 'Remove this vanity and its selected services');
    content.append(button);
    const status = node('p', 'cw-vanity-card__status');
    status.setAttribute('role', 'status');
    content.append(status);
    card.append(content);
    td.append(card);
    tr.append(td);
    return tr;
  }
  function render() {
    for (const tbody of document.querySelectorAll('cart-items tbody, cart-drawer-items tbody')) {
      const groups = new Map();
      const invalid = new Set();
      for (const row of tbody.querySelectorAll('tr[data-cw-vanity-order]')) {
        const order = row.dataset.cwVanityOrder;
        if (!order) continue;
        if (!groups.has(order)) groups.set(order, []);
        try { groups.get(order).push(read(row)); } catch { invalid.add(order); }
      }
      for (const [order, items] of groups) {
        if (invalid.has(order)) continue;
        const fingerprint = JSON.stringify(items.map(item => [item.key, item.quantity, item.total, item.properties, imageData(item)]));
        const existing = [...tbody.querySelectorAll('tr[data-cw-vanity-summary]')].find(row => row.dataset.cwVanitySummary === order);
        if (existing?.dataset.cwFingerprint === fingerprint) continue;
        const summary = build(items, order);
        if (!summary) continue;
        summary.dataset.cwFingerprint = fingerprint;
        existing?.remove();
        items[0].row.before(summary);
        for (const item of items) item.row.hidden = true;
      }
    }
  }
  async function remove(button) {
    const order = button.dataset.cwRemoveVanity;
    const status = button.closest('.cw-vanity-card__content').querySelector('[role="status"]');
    button.disabled = true;
    status.textContent = 'Removing vanity…';
    try {
      const options = { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(15000), headers: { Accept: 'application/json' } };
      const response = await fetch(root() + 'cart.js', options);
      if (!response.ok) throw new Error('Cart unavailable');
      const cart = await response.json();
      if (!Array.isArray(cart.items)) throw new Error('Invalid cart');
      const updates = Object.fromEntries(cart.items.filter(item => item.properties?._cw_vanity_order === order).map(item => [item.key, 0]));
      if (Object.keys(updates).length) {
        const result = await fetch(root() + 'cart/update.js', { ...options, signal: AbortSignal.timeout(15000), method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, body: JSON.stringify({ updates }) });
        if (!result.ok) throw new Error('Remove unavailable');
        const updated = await result.json();
        if (!Array.isArray(updated.items) || updated.items.some(item => item.properties?._cw_vanity_order === order)) throw new Error('Remove unconfirmed');
      }
      window.location.reload();
    } catch {
      status.textContent = 'We could not confirm removal. Refresh your cart to check its status.';
      button.disabled = false;
    }
  }
  let scheduled = false;
  const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => { scheduled = false; render(); });
  };
  function start() {
    render();
    new MutationObserver(records => {
      if (records.some(record => record.type === 'childList' || record.target.matches('tr[data-cw-vanity-order] img'))) schedule();
    }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['src', 'srcset'] });
    document.addEventListener('click', event => {
      const button = event.target.closest('[data-cw-remove-vanity]');
      if (!button || button.disabled) return;
      event.preventDefault();
      remove(button);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
